import type { SvgIconComponent } from '@mui/icons-material'
import AutoGraphRoundedIcon from '@mui/icons-material/AutoGraphRounded'
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded'
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded'
import PsychologyRoundedIcon from '@mui/icons-material/PsychologyRounded'
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded'
import { Chip, Tooltip, type ChipProps } from '@mui/material'

/**
 * GOVIA UX-9 FASE 1 — "Certainty System" (`GOVIA_UX9_MASTER_PRODUCT_
 * DESIGN_PLAN.md` §10/§18). Distingue visualmente HECHO / ANÁLISIS /
 * INFERENCIA / PREDICCIÓN / RECOMENDACIÓN — hoy esa distinción existe solo
 * como discurso (el prompt del backend) o, en un único punto puntual
 * (`ScenarioDialog`, borde punteado + chip "ESCENARIO SIMULADO"), sin un
 * primitivo reutilizable. Este componente es PURA PRESENTACIÓN: no decide
 * ninguna categoría — la recibe ya resuelta por `kind`, desde el dato real
 * o un adapter explícito. Si un consumidor no puede determinar el `kind`
 * con honestidad, la respuesta correcta es NO usar este componente ahí,
 * nunca adivinar uno.
 *
 * Accesibilidad: nunca depende solo del color. Cada estado siempre lleva
 * ícono + texto visible (el `label` es el nombre accesible nativo de
 * `Chip`); los íconos llevan `aria-hidden` para no duplicar el anuncio en
 * lectores de pantalla. Los colores son los YA EXISTENTES del tema
 * (`success`/`secondary`/`info`/`warning`/`primary`, vía el `color` nativo
 * de `Chip` — cero hex nuevo) y deliberadamente NUNCA `error` (reservado
 * para severidad de riesgo — un eje semántico distinto; mezclarlos
 * confundiría "predicción de baja certeza" con "riesgo crítico").
 */
export type CertaintyKind = 'hecho' | 'analisis' | 'inferencia' | 'prediccion' | 'recomendacion'

interface CertaintySpec {
  label: string
  /** Definición formal (encargo, sección "CERTAINTY SYSTEM") — texto de
   * ayuda por defecto en el tooltip. */
  description: string
  icon: SvgIconComponent
  chipColor: NonNullable<ChipProps['color']>
}

// UX-9 FASE 1.1 — semántica obligatoria del encargo (verbatim, no
// reformulada): las 5 descripciones son la definición formal exacta que
// debe mostrarse como tooltip por defecto.
const CERTAINTY_SPEC: Record<CertaintyKind, CertaintySpec> = {
  hecho: {
    label: 'Hecho',
    description: 'Información observada o confirmada mediante evidencia.',
    icon: FactCheckRoundedIcon,
    chipColor: 'success',
  },
  analisis: {
    label: 'Análisis',
    description: 'Interpretación derivada del análisis de información disponible.',
    // "secondary" = cian oficial GOVIA (intelligenceAccent) — mismo acento
    // ya asociado al módulo Inteligencia.
    icon: InsightsRoundedIcon,
    chipColor: 'secondary',
  },
  inferencia: {
    label: 'Inferencia',
    description: 'Conclusión razonada que no está observada directamente.',
    icon: PsychologyRoundedIcon,
    chipColor: 'info',
  },
  prediccion: {
    label: 'Predicción',
    description: 'Estimación sobre una evolución futura con incertidumbre.',
    // "warning" (mismo hex que `color.attention` — nunca `error`/critical):
    // una predicción merece cautela, no alarma.
    icon: AutoGraphRoundedIcon,
    chipColor: 'warning',
  },
  recomendacion: {
    label: 'Recomendación',
    description: 'Opción de acción sugerida a partir de la inteligencia disponible.',
    icon: TipsAndUpdatesRoundedIcon,
    chipColor: 'primary',
  },
}

export function CertaintyBadge({
  kind,
  size = 'small',
  /** Override del texto de ayuda. `null` lo desactiva explícitamente;
   * `undefined` (por defecto) usa la definición formal de la categoría. */
  tooltip,
}: {
  kind: CertaintyKind
  size?: 'small' | 'medium'
  tooltip?: string | null
}) {
  const spec = CERTAINTY_SPEC[kind]
  const Icon = spec.icon
  const chip = (
    <Chip
      size={size}
      variant="outlined"
      color={spec.chipColor}
      icon={<Icon aria-hidden="true" />}
      label={spec.label}
      sx={{ fontWeight: 600 }}
    />
  )
  const helpText = tooltip === null ? null : tooltip ?? spec.description
  return helpText ? (
    <Tooltip title={helpText} arrow placement="top">
      {chip}
    </Tooltip>
  ) : (
    chip
  )
}
